<template>
  <div :class="headerClass">
    <router-link to="/" class="nav-logo">
      <img alt="Acute Immersive logo" src="acute-logo.png" class="logo">
    </router-link>
    <router-link to="/" class="nav-title">
      <div class="title-stack">
        <div class="title-row">
          <span class="wordmark">acute</span>
          <span v-if="!isProd" class="env-tag" :class="tagClass">{{ displayEnvironment }}</span>
        </div>
        <span class="platform-name">Immersive Platform</span>
      </div>
    </router-link>
    <nav v-if="isSuperuser()" class="navbar">
      <!-- Admin links-->
      <router-link to="/admin/videofiles" class="nav-link">Files</router-link>
      <router-link to="/admin/publishing" class="nav-link">Publishing</router-link>
      <router-link to="/admin/channels" class="nav-link">Channels</router-link>
      <router-link to="/admin/organizations" class="nav-link">Organizations</router-link>
      <router-link to="/admin/users" class="nav-link">Users</router-link>
      <router-link to="/admin/permissions" class="nav-link">Permissions</router-link>
      <router-link to="/admin/debugtools" class="nav-link">Tools</router-link>
    </nav>
    <nav v-else-if="authState.isLoggedIn.value" class="navbar">
      <!-- User links-->
      <router-link to="/filemanager" class="nav-link">File Manager</router-link>
      <router-link to="/publishing" class="nav-link">Publishing</router-link>
      <router-link to="/organization" class="nav-link">Organization</router-link>
    </nav>

    <div class="spacer"></div>

    <div v-if="authState.isLoggedIn.value" class="greeting">
      Hello, 
      <router-link to="/users/me/edit" class="nav-link">
        <span class="user-name">{{ authState.userName.value }}</span>
      </router-link>
      <span v-if="isSuperuser()">⭐️</span>
      <span 
        v-if="!authState.isEmailVerified.value" 
        class="verification-warning" 
        title="Your email address has not been verified. Some features may be unavailable until you verify your email."
      >
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
      </span>
  </div>
    <nav v-else class="navbar">
      <router-link to="/login" class="nav-link">
        <font-awesome-icon :icon="['fas', 'sign-in-alt']" />
        Login
      </router-link>
      <router-link to="/signup" class="nav-link">
        <font-awesome-icon :icon="['fas', 'user-plus']" />
        Sign Up
      </router-link>
    </nav>

    <a href="https://apps.apple.com/us/app/acute-pro/id6744127101" target="_blank" rel="noopener noreferrer" class="appstore-button">
      <font-awesome-icon :icon="['fab', 'apple']" class="mr-2" />
      <span>VisionOS App</span>
    </a>

    <a href="https://acuteimmersive.com/documentation" target="_blank" rel="noopener noreferrer" class="help-button">
      <font-awesome-icon :icon="['fas', 'circle-question']" class="mr-2" />
      <span>Help</span>
    </a>
  </div>
</template>

<script setup>
import { authState } from '@/scripts/services/authService';
import { isSuperuser } from '@/scripts/permissions';
import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { getInfo, infoKeys } from '@/scripts/api/infoApi';
import { handleApiError } from '@/scripts/shared-utils';
import { FontAwesomeIcon } from '@/scripts/icons';

const { data: info } = useQuery({
  queryKey: infoKeys.getInfo(),
  queryFn: getInfo,
  staleTime: 60000 * 3600, // One hour, we don't need to refresh this often
  onError: (error) => handleApiError(error, 'fetching environment info')
});

const environment = computed(() => info.value?.environment || 'prod');
const isProd = computed(() => {
  const env = environment.value.toLowerCase();
  return env === 'prod';
});

const displayEnvironment = computed(() => {
  if (isProd.value) {
    // shouldn't happen
    return '';
  }
  return environment.value.toUpperCase();
});

const headerClass = computed(() => {
  if (isProd.value) {
    return 'header';
  }
  return `header ${environment.value.toLowerCase()}`;
});

const tagClass = computed(() => {
  if (isProd.value) {
    // shouldn't happen
    return 'env-tag';
  }
  return `env-tag ${environment.value.toLowerCase()}`;
});
</script>

<style>
@import '@/styles/app.css';
@import '@/styles/shared-buttons.css';
.greeting {
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
}

.user-name {
  font-weight: 600;
}

.verification-warning {
  color: #f39c12;
  cursor: help;
}
</style>
